import React, { useEffect, useRef, useState } from "react";
import { API_BASE } from "./data";
import { fbqTrack } from "./pixel";

// B2B application form (name/contact/YT details + signature & Aadhaar uploads).
// On Save it POSTs multipart data to /api/submit-application; the backend stores
// it, generates the B2B letter and emails it. In the paid flow a Next button then
// hands off to the per-plan checkout.
export default function ApplicationModal({ plan, formOnly = false, standalone = false, onClose, onNext }) {
  const formRef = useRef(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [endDate, setEndDate] = useState("");

  const todayStr = new Date().toISOString().split("T")[0];
  const saved = applicationId !== null;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !standalone) onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, standalone]);

  const subText = formOnly
    ? "Fill in your details to generate your B2B licensing letter."
    : plan === "label"
      ? "Fill in your details to generate your B2B licensing letter, then continue to chat with our team."
      : "Fill in your details to generate your B2B licensing letter, then continue to payment.";

  function handleStartDate(e) {
    if (!e.target.value) { setEndDate(""); return; }
    const d = new Date(e.target.value);
    d.setFullYear(d.getFullYear() + 2);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    setEndDate(`${yyyy}-${mm}-${dd}`);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const form = formRef.current;

    if (!form.checkValidity()) {
      setError("Please fill in all required fields and uploads.");
      form.reportValidity();
      return;
    }
    if (form.start_date.value > form.end_date.value) {
      setError("End date must be on or after the start date.");
      return;
    }

    const fd = new FormData(form);
    fd.append("plan", formOnly ? "" : plan);

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/submit-application`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok || !data.applicationId) throw new Error(data.error || "Submission failed");
      setApplicationId(data.applicationId);
      fbqTrack("Lead", { content_name: plan });
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not save your details. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className={`fmdo-app-modal${standalone ? " b2b-standalone" : ""}`}
      data-lenis-prevent
      onMouseDown={(e) => { if (e.target === e.currentTarget && !standalone) onClose?.(); }}
    >
      <div className="app-card" role="dialog" aria-modal="true" aria-labelledby="fmdo-app-title">
        {!standalone && (
          <button type="button" className="app-close" aria-label="Close" onClick={onClose}>×</button>
        )}
        <h3 id="fmdo-app-title">Start Releasing Today</h3>
        <p className="app-sub">{subText}</p>

        {saved && (
          <div className="app-saved-note">
            {formOnly
              ? <>✓ Thank you! Your details have been submitted and your B2B licensing letter is on its way to your email.</>
              : <>✓ Your details are saved. Your B2B letter is on its way to your email. Click <strong>Next</strong> to pay.</>}
          </div>
        )}

        <form ref={formRef} noValidate onSubmit={handleSubmit}>
          <fieldset disabled={saved} style={{ border: "none", margin: 0, padding: 0 }}>
            <div className="app-field">
              <label>Name <span className="req">*</span></label>
              <input type="text" name="name" required autoComplete="name" />
            </div>
            <div className="app-row">
              <div className="app-field">
                <label>Email <span className="req">*</span></label>
                <input type="email" name="email" required autoComplete="email" />
              </div>
              <div className="app-field">
                <label>Phone <span className="req">*</span></label>
                <input type="tel" name="phone" required autoComplete="tel" />
              </div>
            </div>
            <div className="app-field">
              <label>Address <span className="req">*</span></label>
              <textarea name="address" required />
            </div>
            <div className="app-row">
              <div className="app-field">
                <label>Pincode <span className="req">*</span></label>
                <input type="text" name="pincode" required inputMode="numeric" />
              </div>
              <div className="app-field">
                <label>YT Channel Name <span className="req">*</span></label>
                <input type="text" name="yt_channel" required />
              </div>
            </div>
            <div className="app-field">
              <label>YouTube Link <span className="req">*</span></label>
              <input type="url" name="yt_link" placeholder="https://youtube.com/@..." required />
            </div>
            <div className="app-row">
              <div className="app-field">
                <label>Start Date <span className="req">*</span></label>
                <input type="date" name="start_date" min={todayStr} required onChange={handleStartDate} />
              </div>
              <div className="app-field">
                <label>End Date <span className="req">*</span></label>
                <input type="date" name="end_date" value={endDate} readOnly required
                  title="Automatically set to 2 years after the start date" />
              </div>
            </div>
            <div className="app-field">
              <label>Signature Upload <span className="req">*</span></label>
              <input type="file" name="signature" accept="image/png,image/jpeg,image/webp" required />
            </div>
            <div className="app-row">
              <div className="app-field">
                <label>Aadhaar Front <span className="req">*</span></label>
                <input type="file" name="aadhaar_front" accept="image/*,application/pdf" required />
              </div>
              <div className="app-field">
                <label>Aadhaar Back <span className="req">*</span></label>
                <input type="file" name="aadhaar_back" accept="image/*,application/pdf" required />
              </div>
            </div>
          </fieldset>

          {error && <p className="app-error">{error}</p>}

          <div className="app-actions">
            {!standalone && (
              <button type="button" className="app-btn app-btn-ghost" onClick={onClose}>Cancel</button>
            )}
            {!saved && (
              <button type="submit" className="app-btn app-btn-primary" disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            )}
            {saved && !formOnly && (
              <button type="button" className="app-btn app-btn-primary"
                onClick={() => onNext?.(applicationId)}>
                Next →
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
